import ListData, { ColumnConfig, ColumnFilter, ListChoix, ListConfig, ListFilter } from "@/extend/classes/ListData";
import { notifClass } from '@/framework/base/helpers/Notif';
import { Extended } from "@/framework/base/classes/Schema";
import Route from "@/extend/classes/Route";
import { isValidDate } from "@/framework/base/helpers/DateFormat";
import Button from '@/framework/base/classes/Button';
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import { FieldStatus } from "./FieldInfo";
import { defineComponent, type PropType } from "vue";
import ListHeader from '@/framework/base/components/ListHeader.vue';
import Etablissement from "@/extend/classes/Etablissement.ts";
import {Fiches, Modules} from "@/generated/classes/AppSchema.ts";

export default defineComponent({
  components: {
    LoaderButton,
    ListHeader
  },
  props: {
    attribut: {
      type: Object as PropType<unknown> | undefined
    },
    route: {
      type: Object as PropType<Route>,
    },
    isAbstract: {
      type: Boolean,
    }
  },
  data() {
    const searchTimeout: any = null;
    const lastAttribut: unknown = undefined;
    const activeFilters: ColumnFilter[] = [];
    const pageNumber = 1;
    return {
      listData: new ListData(),
      activeFilters,
      lastAttribut,
      searchText: '',
      pageNumber: 1,
      loading: false,
      lastRoute: new Route(),
      searchTimeout,
      isCreated: false,
      fullscreen: false,
      status: new FieldStatus(false, false, 0),
      copiedField: null as number | null,
      isCardView: false,
      sortBy: [] as Array<{ key: string; order: 'asc' | 'desc' }>,
    };
  },
  computed: {
    headers() {
      return [{ text: "", value: '' }];
    },
    displayedHeaders(): string[] {
      const res: string[] = [];
      this.headers.forEach((item) => {
        res.push(item.value);
      })
      return res;
    },
    showSelect(): boolean {
      return false;
    },
    filter(): ListFilter {
      const filter = new ListFilter();
      filter.page = this.pageNumber;
      filter.displayedHeaders = this.displayedHeaders;
      filter.searchText = this.searchText;
      filter.columnsFilter = this.activeFilters;
      filter.pageLimit = this.pageLimit;
      if (this.route && this.route.sectionObjId > 0) {
        filter.selectedId = this.route.sectionObjId;
      }
      return filter;
    },
    config(): ListConfig {
      return this.listData.config;
    },
    pageLimit(): number {
      return 20;
    },
    listConfig(): ColumnConfig[] {
      return this.config.columnsConfig;
    },
    pageCount(): number {
      return this.listData.pageCount;
    },
    totalCount(): number {
      return this.listData.totalCount;
    },
    filteredCount(): number {
      return this.listData.filteredCount;
    },
    page: {
      get(): number {
        return this.pageNumber;
      },
      set(page: number) {
        this.pageNumber = page;
        this.reloadTable();
      }
    },
    extraButtons(): Button[] {
      const buttons: Button[] = [];
      return buttons;
    }
  },
  methods: {
    getSortOrder(key: string): 'asc' | 'desc' | null {
      const s = this.sortBy?.find(x => x.key === key);
      return s?.order ?? null;
    },
    defaultFilter(): void {
      // To oveeride
    },
    resetActiveFilters(name: string): void {
      this.activeFilters.forEach((item) => {
        if (item.name == name) {
          item.filters = [];
        }
      })
      this.defaultFilter();
    },
    reloadTable(): void {
      this.loadTable();
    },
    loadTable(): void {
      // To oveeride
    },
    onSearchTextUpdated(text: string): void {
      clearTimeout(this.searchTimeout);
      this.searchText = text;
      this.searchTimeout = setTimeout(() => this.setUpdated(), 300);
    },
    getFilters(value: string): ListChoix[] {
      const res: ListChoix[] = [];
      this.activeFilters.forEach((item) => {
        if (item.name == value) {
          item.filters.forEach(element => {
            res.push(element)
          })
        }
      })
      return res;
    },
    currentFilters(): void {
      if (this.attribut != this.lastAttribut) {
        this.lastAttribut = this.attribut;
        this.activeFilters = [];
        this.defaultFilter();
        if (this.attribut && (this.attribut instanceof ListFilter)) {
          const attrib = this.attribut as ListFilter;
          attrib.columnsFilter.forEach((item) => {
            this.activeFilters.push(item);
          })
        }
      }
    },
    filterCondition(): string {
      let filterCondition = 'a => ';
      this.activeFilters.forEach((activeFilter, index) => {
        if (activeFilter.filters.length > 0) {
          if (index > 0) {
            filterCondition += ' && ';
          }
          filterCondition += '(a.' + activeFilter.name + '== ';
          activeFilter.filters.forEach((choix, index) => {
            if (index == 0) {
              filterCondition += choix.value
            } else if (index > 0) {
              filterCondition += '||' + 'a.' + activeFilter.name + '==' + choix.value;
            }
          })
          filterCondition += ')';
        }
      })
      return filterCondition;
    },
    search(list: any[]): any[] {
      // eslint-disable-next-line
      const filteredList: any[] = [];
      if (this.searchText) {
        list.forEach((item) => {
          Object.entries(item).forEach(entry => {
            const [key, value] = entry;
            if (this.headers.find(a => a.value == key)) {
              // @ts-expect-error : index is defined
              const res = this.getLabel(key, value);
              if (res.toLowerCase().includes(this.searchText.toLowerCase())) {
                if (!filteredList.includes(item)) {
                  filteredList.push(item);
                }
              }
            }
          });
        });
        return filteredList;
      } else {
        return list;
      }
    },
    onSortUpdate (newValue: any): void {
      if (newValue?.length > 0) {
        const index = [newValue[0].key];
        const isDesc = [newValue[0].order == 'desc'];
        this.customSort(index, isDesc);
      }
    },
    customSort(index: any, isDesc: any): void {
      let filterFound = false;
      let reload = false;
      if (index.length > 0) {
        this.activeFilters.forEach((item) => {
          if (item.name != index) {
            item.sortAscending = false;
            item.sortDescending = false;
          }
        })
        if (this.activeFilters.length > 0) {
          const activeFilter = this.activeFilters.find(a => a.name == index[0]);
          if (activeFilter != undefined) {
            if (activeFilter.sortAscending != !isDesc[0] || activeFilter.sortDescending != isDesc[0]) {
              reload = true;
            }
            this.activeFilters.forEach((item) => {
              if (item.name == index[0]) {
                item.sortAscending = !isDesc[0];
                item.sortDescending = isDesc[0];
                filterFound = true;
              }
            })
          }
        }

        if (!filterFound) {
          reload = true;
          const columnFilter = new ColumnFilter();
          columnFilter.name = index[0];
          columnFilter.sortAscending = !isDesc[0];
          columnFilter.sortDescending = isDesc[0];
          this.activeFilters.push(columnFilter);
        }
        if (reload) {
          this.reloadTable();
        }
      }
    },
    getConfig(value: string): ColumnConfig {
      let columnConfig = new ColumnConfig();
      this.listConfig.forEach((item) => {
        if (item.name == value) {
          columnConfig = item;
        }
      })
      return columnConfig;
    },
    extendLabel(name: string, item: number, obj: unknown): string | undefined {
      return undefined;
    },
    getLabel(name: string, item: number, obj: unknown): string {
      let res = this.extendLabel(name, item, obj);
      if (!res) {
        res = item ? item.toString() : '';
        const column = this.listConfig.find(a => a.name == name);
        if (column) {
          const choix = column.choix.find(a => a.value == item);
          if (choix) {
            res = choix.text;
          }
        }
        if (isValidDate(res)) {
          res = new Date(res).toString()
        }
      }
      return res;
    },
    getClass(item: any): string {
      return notifClass(item.notifDefType);
    },
    setExtended(extended: Extended): void {
      this.$emit('extended', extended);
    },
    applyFilters(activeFilters: ListChoix[], name: string) {
      if (activeFilters.length > 0) {
        let columnFilter = this.activeFilters.find(a => a.name == name);
        if (columnFilter != undefined) {
          columnFilter.filters = [];
          activeFilters.forEach((item) => {
            // @ts-expect-error : columnFilter not undefined
            columnFilter.filters.push(item);
          })
        } else {
          columnFilter = new ColumnFilter();
          columnFilter.name = name;
          activeFilters.forEach((item) => {
            // @ts-expect-error : columnFilter not undefined
            columnFilter.filters.push(item);
          })
          this.activeFilters.push(columnFilter);
        }
      } else {
        this.resetActiveFilters(name);
      }
      this.setUpdated();
    },
    copyText(text: string, fieldIndex: number): void {
      const textToCopy = text.trim();
      navigator.clipboard.writeText(textToCopy).then(() => {
        this.copiedField = fieldIndex;
        setTimeout(() => this.copiedField = null, 1500);
      }).catch((err) => {
        console.error('Failed to copy:', err);
      });
    },
    cardView(value: boolean): void {
      if (this.isCardView != value) {
        this.isCardView = !this.isCardView;
        this.$emit('toggleCardView', this.isCardView);
      }
    },
    selectItemCard(item: any): void {
      // to override
    },
    getInitials(value: any): string {
      return "";
    },
    getTitle(value: any, obj: any): string {
      return value;
    },
    setUpdated(): void {
      this.pageNumber = 1;
      this.reloadTable();
    }
  }
})
